import { useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';
import { panelsSelector, selectCurrentOrganization, Panel } from 'store';
import { PanelSummary } from './components/PanelSummary';
import { AddPanelModal } from './components/AddPanelModal';
import { Button, DeletableItem, SquareButton } from 'components';
import { useBoolModalState } from '@app/core';
import { DeletePanelModal } from './components/DeletePanelModal';
import { SettingContent } from './components/SettingContent';

const useStyles = createUseStyles((theme: Theme) => ({
  title: {
    ...theme.fonts.h2,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.marginBase * 3,
  },
  buttonsContainer: {
    right: -theme.marginBase * 6,
    top: -theme.marginBase,
  },
  topButton: {
    marginBottom: theme.marginBase,
    [theme.media.tablet]: {
      marginBottom: 0,
      marginRight: theme.marginBase,
    },
  },
}));

export const Panels = () => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const [addModalOpened, openAddModal, closeAddModal] = useBoolModalState();
  const [deleteModalOpened, openDeleteModal, closeDeleteModal] =
    useBoolModalState();

  const panels = useSelector(panelsSelector);
  const organization = useSelector(selectCurrentOrganization);

  const [selectedPanel, setSelectedPanel] = useState<Partial<Panel> | null>(
    null,
  );
  const [edition, setEdition] = useState(false);

  return (
    <SettingContent>
      <div className={classes.header}>
        <h1 className={classes.title}>{t('settings.panels.title')}</h1>
        <div>
          <Button
            text={t('settings.panels.addButton')}
            onClick={openAddModal}
            color="accent"
          />
          <AddPanelModal
            organizationId={organization && organization.id}
            onRequestClose={closeAddModal}
            isOpen={addModalOpened}
          />
        </div>
      </div>
      <div>
        {panels.map((panel) => (
          <DeletableItem
            buttonsClassName={classes.buttonsContainer}
            onDeleteClick={() => {
              setSelectedPanel(panel);
              openDeleteModal();
            }}
            topButtons={
              <SquareButton
                icon="ri-edit-line"
                color="blue"
                className={classes.topButton}
                onClick={() => {
                  setEdition(true);
                  setSelectedPanel(panel);
                }}
              />
            }
            key={panel.id}
          >
            <PanelSummary
              panelName={panel.name}
              isEdition={
                edition && !!selectedPanel && selectedPanel.id === panel.id
              }
              panelId={panel.id}
              setEdition={setEdition}
            />
          </DeletableItem>
        ))}
        <DeletePanelModal
          onRequestClose={closeDeleteModal}
          isOpen={deleteModalOpened}
          panel={selectedPanel}
        />
      </div>
    </SettingContent>
  );
};
